import type * as Api from '@ViewModels';
import { useCallback } from 'react';

import { useUserSession } from '../../models/hooks/appStateHooks';
import { useUserPreferencesMutation } from '../../queries/User/useUserPreferencesMutation';

/**
 * `coffeePreferences.uiVersion` value meaning the rep has opted into the new
 * Focused Lead View. Any other value (including unset) means the legacy UI.
 * Exported so non-hook consumers (e.g. `telephony.tsx`) can derive the opt-in
 * off the user session without duplicating the literal.
 */
export const UI_VERSION_FOCUSED = 'focused-view';
export const UI_VERSION_LEGACY = 'legacy';

export const isFocusedViewOn = (coffeePreferences?: Api.ICoffeePreferences) =>
	coffeePreferences?.uiVersion === UI_VERSION_FOCUSED || coffeePreferences?.focusedViewLocked === true;

/**
 * Server-backed opt-in for the Focused Lead View.
 *
 * The source of truth is the acting user's `coffeePreferences.uiVersion`,
 * persisted via the user-preferences endpoint and mirrored onto the MobX
 * `userSession`. Anchoring to a stored preference (rather than a client-only
 * flag) lets the backend attribute phone-call activity — including inbound
 * calls, which carry no frontend request — to the rep's current UI.
 *
 * Reactivity: `enabled` is derived from the observable session, so consumers
 * that must reflect live toggles need to be MobX `observer`s (e.g. `Queue`,
 * `LeadCard`). `setFlag` updates the session optimistically for instant
 * feedback, then persists; the server response reconciles on success.
 */
export function useFocusedViewEnabled(): [boolean, (next: boolean) => void] {
	const userSession = useUserSession();
	const enabled = isFocusedViewOn(userSession.user?.userPreferences?.coffeePreferences);

	const prefsMutation = useUserPreferencesMutation({
		onSuccess: (next: Api.IUserPreferences) => {
			// Reconcile with the server's merged preferences (mirrors the legacy
			// queue / coaching-prefs pattern) so later reads see the canonical copy.
			if (userSession.user) {
				userSession.user.userPreferences = next;
			}
		},
		// Background preference write — failures must not surface to the rep, but
		// `useMutation` has no MutationCache wiring so an explicit handler is
		// required (see packages/main/CLAUDE.md). Swallow intentionally.
		onError: () => {},
	});

	const setFlag = useCallback(
		(next: boolean) => {
			const uiVersion = next ? UI_VERSION_FOCUSED : UI_VERSION_LEGACY;
			const currentPreferences = userSession.user?.userPreferences;
			const nextCoffeePreferences = { ...currentPreferences?.coffeePreferences, uiVersion };

			// Optimistic: update the observable session immediately so `observer`
			// consumers reflect the toggle without waiting for the round-trip. The
			// spread preserves other coffee preferences (the backend replaces the
			// whole object). `onSuccess` reconciles with the server's copy.
			if (userSession.user) {
				userSession.user.userPreferences = {
					...currentPreferences,
					coffeePreferences: nextCoffeePreferences,
				} as Api.IUserPreferences;
			}

			prefsMutation.mutate({
				preferences: { coffeePreferences: nextCoffeePreferences } as Api.IUserPreferences,
			});
		},
		[prefsMutation, userSession]
	);

	return [enabled, setFlag];
}

export function useFocusedViewLocked() {
	const userSession = useUserSession();
	return userSession.user?.userPreferences?.coffeePreferences?.focusedViewLocked === true;
}
